import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Field, FormSection, SelectInput } from '@/components/Admin/AdminFormFields';
import { HeroImageField } from '@/components/Admin/HeroImageField';
import { RichTextField } from '@/components/Admin/RichTextField';
import { SlugField } from '@/components/Admin/SlugField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  createCollection,
  getCollection,
  isCollectionIdTaken,
  saveCollection,
} from '@/data/collectionsRepo';
import { DocumentIdTakenError } from '@/data/util_documentIds';
import {
  collectionStatusLabels,
  hasBio,
  makeEmptyBio,
  makeEmptyCollection,
  type CollectionBioType,
  type CollectionStatusType,
  type CollectionType,
} from '@/types/types_collection';
import { experienceRegionLabels, type ExperienceRegionType } from '@/types/types_experience';
import { slugify, validateSlugForCreate } from '@/util/util_slug';

interface PageCollectionEditProps {
  mode: 'new' | 'edit';
}

/** Create / edit form for a single collection. */
export function PageCollectionEdit({ mode }: PageCollectionEditProps) {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<CollectionType>(() => makeEmptyCollection());
  // The bio is bound to a always-present object so the inputs stay controlled;
  // it is only saved onto the collection when something has been typed into it.
  const [bio, setBio] = useState<CollectionBioType>(() => makeEmptyBio());
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Why the Create button refused, shown above it, and the web address field's
  // own complaint. Both `new` mode only: an existing collection's id is fixed.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'edit' || !collectionId) return;
    let cancelled = false;
    getCollection(collectionId)
      .then((c) => {
        if (cancelled) return;
        if (c) {
          setForm(c);
          if (c.bio) setBio(c.bio);
        } else setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, collectionId]);

  function update<K extends keyof CollectionType>(key: K, value: CollectionType[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateBio<K extends keyof CollectionBioType>(key: K, value: CollectionBioType[K]) {
    setBio((prev) => ({ ...prev, [key]: value }));
  }

  // The web address, written straight onto the form. Stable so that SlugField's
  // effect — which fills the field in from the name — does not re-run per render.
  const setSlug = useCallback((slug: string) => setForm((prev) => ({ ...prev, id: slug })), []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    // An edit keeps the id it already has — the address is in circulation — so
    // only a new collection has one to claim, and only it can clash.
    if (mode === 'edit') {
      try {
        await saveCollection({ ...form, bio: hasBio(bio) ? bio : undefined });
      } catch (e: unknown) {
        console.error('Save collection failed', e);
        setSaveError('The changes could not be saved. Try again.');
        setSaving(false);
        return;
      }
      setSaving(false);
      navigate('/admin/collections');
      return;
    }

    // Checked again here, not only on blur: the field validates what was in it
    // when the user left it, and the catalogue may have moved on since.
    const id = slugify(form.id || form.name);
    const idError = await validateSlugForCreate(id, isCollectionIdTaken, 'collection');
    if (idError) {
      setSlugError(idError);
      setSaveError('Check the highlighted field above.');
      setSaving(false);
      return;
    }

    try {
      await createCollection({ ...form, id, bio: hasBio(bio) ? bio : undefined });
    } catch (e: unknown) {
      // The id was free a moment ago and is not any more — somebody else created
      // it in between, and the transaction refused rather than writing over them.
      if (e instanceof DocumentIdTakenError) {
        setSlugError('That web address was taken while this form was open. Choose a different one.');
        setSaveError('Check the highlighted field above.');
      } else {
        console.error('Create collection failed', e);
        setSaveError('The collection could not be created. Try again.');
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    navigate('/admin/collections');
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  if (notFound) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>Collection not found.</p>
        <Link to='/admin/collections' className='tw:mt-4 tw:inline-block tw:text-primary tw:underline'>
          Back to collections
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-8'>
        <Link
          to='/admin/collections'
          className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
        >
          <ArrowLeft className='tw:size-4' /> Collections
        </Link>
        <h1 className='tw:mt-2 tw:font-serif tw:text-3xl tw:font-light'>
          {mode === 'new' ? 'Add New Collection' : `Edit — ${form.name}`}
        </h1>
      </header>

      <div className='tw:space-y-8'>
        <FormSection title='Basics'>
          <Field label='Collection Name' required className='tw:sm:col-span-2'>
            <Input value={form.name} onChange={(e) => update('name', e.target.value)} required />
          </Field>
          {mode === 'new' && (
            <SlugField
              label='Web Address'
              name={form.name}
              value={form.id}
              onChange={setSlug}
              isTaken={isCollectionIdTaken}
              pathPrefix='/collections/'
              entityLabel='collection'
              error={slugError}
              onErrorChange={setSlugError}
            />
          )}
          <Field label='Subtitle' className='tw:sm:col-span-2'>
            <Input value={form.subtitle} onChange={(e) => update('subtitle', e.target.value)} />
          </Field>

          <Field label='Region' required>
            <SelectInput
              value={form.region}
              options={experienceRegionLabels}
              onValueChange={(v) => update('region', v as ExperienceRegionType)}
            />
          </Field>
          <Field label='Location / City' required>
            <Input value={form.location} onChange={(e) => update('location', e.target.value)} required />
          </Field>

          <Field label='Status'>
            <SelectInput
              value={form.status}
              options={collectionStatusLabels}
              onValueChange={(v) => update('status', v as CollectionStatusType)}
            />
          </Field>

          <Field label='Hero Image' className='tw:sm:col-span-2'>
            <HeroImageField value={form.heroImage} onChange={(url) => update('heroImage', url)} />
          </Field>
        </FormSection>

        <FormSection title='Experiences Heading'>
          <p className='tw:sm:col-span-2 tw:-mt-1 tw:text-sm tw:text-muted-foreground'>
            Introduces the list of experiences on the public collection page. Leave both blank to show the
            experiences with no heading above them.
          </p>

          <Field label='Title' className='tw:sm:col-span-2'>
            <Input
              value={form.experiencesTitle}
              placeholder='Exclusive Experiences'
              onChange={(e) => update('experiencesTitle', e.target.value)}
            />
          </Field>

          <Field label='Subtitle' className='tw:sm:col-span-2'>
            <Textarea
              rows={2}
              value={form.experiencesSubtitle}
              placeholder='One line on what the collection offers.'
              onChange={(e) => update('experiencesSubtitle', e.target.value)}
            />
          </Field>
        </FormSection>

        <FormSection title='Bio (optional)'>
          <p className='tw:sm:col-span-2 tw:-mt-1 tw:text-sm tw:text-muted-foreground'>
            Introduces the person behind the collection — a chef, a guide, an artisan. Shown above the
            experiences on the public collection page. Leave blank for collections with no one to introduce.
          </p>

          <Field label='Eyebrow'>
            <Input
              value={bio.eyebrow}
              placeholder='Meet the Chef'
              onChange={(e) => updateBio('eyebrow', e.target.value)}
            />
          </Field>
          <Field label='Portrait'>
            <HeroImageField
              value={bio.image}
              folder='guides'
              onChange={(url) => updateBio('image', url)}
            />
          </Field>

          <Field label='Name'>
            <Input value={bio.name} onChange={(e) => updateBio('name', e.target.value)} />
          </Field>
          <Field label='Secondary Name'>
            <Input
              value={bio.secondaryName}
              placeholder='e.g. the romanised name'
              onChange={(e) => updateBio('secondaryName', e.target.value)}
            />
          </Field>

          <Field label='Badge Value'>
            <Input
              value={bio.badgeValue}
              placeholder='20+'
              onChange={(e) => updateBio('badgeValue', e.target.value)}
            />
          </Field>
          <Field label='Badge Label'>
            <Input
              value={bio.badgeLabel}
              placeholder='Years Experience'
              onChange={(e) => updateBio('badgeLabel', e.target.value)}
            />
          </Field>

          <Field label='Bio' className='tw:sm:col-span-2'>
            <RichTextField
              value={bio.text}
              placeholder='Their background, training, awards…'
              onChange={(html) => updateBio('text', html)}
            />
          </Field>
        </FormSection>
      </div>

      {saveError && (
        <p className='tw:mt-6 tw:text-right tw:text-sm tw:text-destructive'>{saveError}</p>
      )}

      <div className='tw:mt-8 tw:flex tw:justify-end tw:gap-3'>
        <Button asChild variant='outline' type='button'>
          <Link to='/admin/collections'>Cancel</Link>
        </Button>
        <Button type='submit' disabled={!form.name.trim()} loading={saving}>
          {saving ? 'Saving…' : mode === 'new' ? 'Create Collection' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
